import { useEffect, useRef, useState } from 'react';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { CheckIcon, CopyIcon, DownloadIcon, PlusIcon, QrCodeIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import { FieldLabel, Overline } from '@/components/ui/text';
import { IconButton } from '@/components/ui/icon-button';
import { QrCode } from '@/components/ui/qr-code';
import { TextButton } from '@/components/ui/text-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { TAKEN_DOWN_NOTE, useShareLink } from '@/hooks/useShareLink';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { useOrganization } from '@/hooks/useOrganization';
import { downloadQrPng, qrSizeForImageWidth } from '@/lib/downloadQrPng';
import { ApiProblem } from '@/infrastructure/api/v1/problem';
import type { AnchorCategory, PublishDevice } from './types';

interface SharePanelProps {
  sceneInstanceId?: string;
  partialBlocks?: Partial<Record<PublishDevice, string>>;
  qrSize?: number;
  anchorCategory?: AnchorCategory;
  onRequestImmersalToken?: () => void;
  offerSpectaclesCode?: boolean;
}

/**
 * The body shared by the editor's Test dialog, the story page's Share card, the publish flow's
 * confirmation step and each story row of the project dialog. A story gets one link: its preview
 * link while the version is unpublished, the story's permanent link once live, plus the
 * Spectacles code when the author chose Spectacles (share links 3.2), or on request where
 * `offerSpectaclesCode` is set.
 */
export function SharePanel(props: SharePanelProps) {
  return props.sceneInstanceId ? <SceneSharePanel {...props} sceneInstanceId={props.sceneInstanceId} /> : null;
}

function useCopy(sceneInstanceId: string | undefined, linkKind: string | undefined) {
  const services = useServices();
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);

  const copyText = async (text: string, kind: 'link' | 'code') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
      services.analytics.trackLureoSceneLinkCopied({
        scene_instance_id: sceneInstanceId,
        link_type: kind === 'code' ? 'spectacles' : linkKind === 'live' ? 'share' : 'draft_share',
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return { copied, copyText };
}

// Creating the link is the story page's job (the destinations card), so the QR area only points there
const NO_LIVE_LINK_HERE = 'No public link right now. Create one under Where this story is published.';

function RetryButton({ onClick }: { onClick: () => void }) {
  return <TextButton onClick={onClick}>Try again</TextButton>;
}

// Print size, whatever the on-screen QR is: it is an SVG, so the PNG is drawn crisp at any size.
// 1024 is the whole image, white margin included
const DOWNLOAD_QR_SIZE = qrSizeForImageWidth(1024);

function DownloadButton({ target, filename }: { target: React.RefObject<HTMLDivElement | null>; filename: string }) {
  return (
    <LureoButton
      variant="outline-3"
      suppressTooltip
      onClick={() => downloadQrPng(target.current, DOWNLOAD_QR_SIZE, filename)}
      className="h-8 rounded-lureo-radius-button-pill px-3 gap-2 text-lureo-13"
    >
      <DownloadIcon className="h-3.5 w-3.5" />
      Download
    </LureoButton>
  );
}

function CopyCodeButton({ copied, onClick, className }: { copied: boolean; onClick: () => void; className?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <IconButton label="Copy code" onClick={onClick} className={className}>
          {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <CopyIcon className="h-3.5 w-3.5" />}
        </IconButton>
      </TooltipTrigger>
      <TooltipContent sideOffset={10}>{copied ? 'Copied!' : 'Copy code'}</TooltipContent>
    </Tooltip>
  );
}

function Placeholder({ size, children }: { size: number; children: React.ReactNode }) {
  return (
    <div
      className="flex items-center justify-center rounded-lureo-radius border-lureo-border-width border-lureo-line bg-lureo-neutral-subtle text-center font-lureo-font-prose text-lureo-13 text-lureo-ink-subtle px-4"
      style={{ width: size + 24, height: size + 24 }}
    >
      {children}
    </div>
  );
}

function SceneSharePanel({
  sceneInstanceId,
  qrSize = 200,
  partialBlocks,
  anchorCategory,
  onRequestImmersalToken,
  offerSpectaclesCode,
}: SharePanelProps & { sceneInstanceId: string }) {
  const { link, url, ready, error, errorCode, reason, canCreate, forSpectacles, refetch } = useShareLink(sceneInstanceId);
  const { orgInstance } = useOrganization();
  const { copied, copyText } = useCopy(sceneInstanceId, link?.kind);
  const [codeRevealed, setCodeRevealed] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  const showCode = forSpectacles || codeRevealed;
  const isImmersalScene = anchorCategory === 'immersal';
  const needsImmersalToken =
    isImmersalScene && Boolean(onRequestImmersalToken) && !orgInstance?.orgInstanceImmersalConfigured;
  const notes = Object.values(partialBlocks ?? {}).filter((m): m is string => Boolean(m));

  const caption =
    link?.kind === 'draft'
      ? showCode && link.pinCode
        ? 'A preview link for testing. Anyone with this link can open this version until you publish or swap the link. Swapping the link keeps the Spectacles code.'
        : 'A preview link for testing. Anyone with this link can open this version until you publish or swap the link.'
      : link?.kind === 'live'
        ? 'Your story’s permanent link. Every copy you share or print stays valid until you revoke it.'
        : '';

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      {(caption || notes.length > 0 || needsImmersalToken) && (
        <div className="flex flex-col items-center gap-1 -mt-1">
          {caption && (
            <p className="font-lureo-font-prose text-lureo-13 text-lureo-ink-subtle leading-[1.4] text-center max-w-[22rem]">{caption}</p>
          )}
          {needsImmersalToken && (
            <div className="flex flex-col items-center gap-0.5">
              <p className="font-lureo-font-ui font-semibold text-lureo-13 text-lureo-warning-ink leading-[1.4] text-center">
                Android will need your Immersal token to work on web.
              </p>
              <TextButton onClick={() => onRequestImmersalToken?.()}>Add developer token</TextButton>
            </div>
          )}
          {notes.map((note) => (
            <p key={note} className="font-lureo-font-ui font-semibold text-lureo-13 text-lureo-warning-ink leading-[1.4] text-center">
              {note}
            </p>
          ))}
        </div>
      )}

      {!ready ? (
        <Placeholder size={qrSize}>Preparing link…</Placeholder>
      ) : url ? (
        <QrCode ref={qrRef} value={url} size={qrSize} />
      ) : (
        <Placeholder size={qrSize}>
          {errorCode === 'taken_down' ? (
            TAKEN_DOWN_NOTE
          ) : error ? (
            <span className="flex flex-col items-center gap-1">
              <span>Link unavailable</span>
              <RetryButton onClick={refetch} />
            </span>
          ) : canCreate ? (
            NO_LIVE_LINK_HERE
          ) : (
            reason ?? 'Link unavailable'
          )}
        </Placeholder>
      )}

      {url && (
        <p className="font-lureo-font-mono text-lureo-12 text-lureo-ink-subtle break-all text-center max-w-[22rem]">{url}</p>
      )}

      {!canCreate && (
        <div className="flex items-center gap-2">
          {url && <DownloadButton target={qrRef} filename={`lureo-qr-${link?.slug ?? sceneInstanceId}`} />}
          <LureoButton
            variant="outline-3"
            suppressTooltip
            onClick={() => url && copyText(url, 'link')}
            disabled={!url}
            className="h-8 rounded-lureo-radius-button-pill px-3 gap-2 text-lureo-13"
          >
            {copied === 'link' ? <CheckIcon className="h-3.5 w-3.5" /> : <CopyIcon className="h-3.5 w-3.5" />}
            {copied === 'link' ? 'Copied' : 'Copy link'}
          </LureoButton>
        </div>
      )}

      <div className="h-11 flex items-center">
        {link?.pinCode && !showCode && offerSpectaclesCode && (
          <TextButton onClick={() => setCodeRevealed(true)}>Testing on Spectacles?</TextButton>
        )}
        {link?.pinCode && showCode && (
          <div className="flex items-center gap-3 rounded-lureo-radius-pill border-lureo-border-width border-lureo-line bg-lureo-neutral-subtle pl-4 pr-1.5 py-1.5">
            <FieldLabel>Spectacles code</FieldLabel>
            <p className="font-lureo-font-mono font-semibold text-lureo-16 tracking-[0.3em] text-lureo-ink pl-1 leading-none">
              {link.pinCode}
            </p>
            <CopyCodeButton copied={copied === 'code'} onClick={() => copyText(link.pinCode!, 'code')} />
          </div>
        )}
      </div>
    </div>
  );
}

// The compact QR's size range, and the white frame around it (p-2 plus the 1px border)
const COMPACT_QR_MIN = 96;
const COMPACT_QR_MAX = 176;
const COMPACT_QR_FRAME = 18;

/**
 * The project dialog's version of the panel: the same link, code and actions, laid out to fit a
 * card the width of half a story row. The QR fills the height of the details beside it; those are
 * built to a fixed height (two-line note, stacked buttons), so the QR growing never re-wraps them.
 * No URL text: half a row cannot show it whole, and Copy link carries it. Unlike the story page it offers Create link itself, because the dialog has no
 * destinations card to send the creator to.
 */
export function CompactSharePanel({ sceneInstanceId }: { sceneInstanceId: string }) {
  const { link, url, ready, error, errorCode, reason, canCreate, createLive, forSpectacles, refetch } = useShareLink(sceneInstanceId);
  const { copied, copyText } = useCopy(sceneInstanceId, link?.kind);
  const qrRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const [qrSize, setQrSize] = useState(COMPACT_QR_MIN);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const details = detailsRef.current;
    if (!details) return;
    // Fires once on observe, then again if the details change height (a font arriving, a new state)
    const observer = new ResizeObserver(([entry]) => {
      const height = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
      setQrSize(Math.min(COMPACT_QR_MAX, Math.max(COMPACT_QR_MIN, height - COMPACT_QR_FRAME)));
    });
    observer.observe(details);
    return () => observer.disconnect();
  }, []);

  const isLive = link?.kind === 'live';
  const takenDown = errorCode === 'taken_down';
  const title = !ready
    ? 'Preparing link…'
    : url
      ? isLive ? 'Public link' : 'Preview link'
      : takenDown ? 'Taken down' : canCreate ? 'No public link' : 'Link unavailable';
  const note = !ready
    ? ''
    : url
      ? isLive ? 'Valid until you revoke it.' : 'Works until you publish or swap it.'
      : takenDown
        ? TAKEN_DOWN_NOTE
        : canCreate
          ? 'Create one to share this story.'
          : error ? 'Could not load the link.' : reason ?? '';

  const actionClass = 'h-7 w-full rounded-lureo-radius-button-pill px-3 gap-1.5 text-lureo-12';

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-3">
        {url ? (
          <QrCode ref={qrRef} value={url} size={qrSize} frame="sm" className="shrink-0 self-start" />
        ) : (
          // ui-exempt: QrCode's frame (its radius and p-2), holding the code's place until there's a link so nothing moves
          <div className="shrink-0 self-start rounded-lureo-radius border-lureo-border-width border-lureo-line bg-lureo-surface p-2">
            <div
              className="flex items-center justify-center rounded-lureo-radius-lg bg-lureo-neutral text-lureo-ink-subtlest"
              style={{ width: qrSize, height: qrSize }}
            >
              {ready ? <QrCodeIcon className="h-8 w-8" /> : <LoadingSpinner size="md" />}
            </div>
          </div>
        )}

        {/* self-start: measured at its own height, never stretched to the QR beside it (that would feed back) */}
        <div ref={detailsRef} className="flex min-w-0 flex-1 flex-col gap-1.5 self-start">
          <Overline size="label" as="span">{title}</Overline>
          <p className="line-clamp-2 min-h-[2.8em] font-lureo-font-prose text-lureo-12 leading-[1.4] text-lureo-ink-subtle">{note}</p>

          <div className="flex flex-col gap-1.5 pt-0.5">
            {url ? (
              <>
                <LureoButton
                  variant="outline-3"
                  suppressTooltip
                  onClick={() => downloadQrPng(qrRef.current, DOWNLOAD_QR_SIZE, `lureo-qr-${link?.slug ?? sceneInstanceId}`)}
                  className={actionClass}
                >
                  <DownloadIcon className="h-3.5 w-3.5" />
                  Download
                </LureoButton>
                <LureoButton variant="outline-3" suppressTooltip onClick={() => copyText(url, 'link')} className={actionClass}>
                  {copied === 'link' ? <CheckIcon className="h-3.5 w-3.5" /> : <CopyIcon className="h-3.5 w-3.5" />}
                  {copied === 'link' ? 'Copied' : 'Copy link'}
                </LureoButton>
              </>
            ) : canCreate ? (
              <>
                <LureoButton
                  variant="outline-3"
                  suppressTooltip
                  disabled={creating}
                  onClick={async () => {
                    setCreating(true);
                    try {
                      await createLive();
                    } catch (err) {
                      console.error('createLive failed:', err);
                      toast.error(err instanceof ApiProblem && err.code === 'taken_down' ? TAKEN_DOWN_NOTE : 'Could not create the link');
                    } finally {
                      setCreating(false);
                    }
                  }}
                  className={actionClass}
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  {creating ? 'Creating…' : 'Create link'}
                </LureoButton>
                {/* Holds the second button's row, so the card keeps its height whatever the state */}
                <span aria-hidden className="h-7" />
              </>
            ) : error && !takenDown ? (
              <>
                <LureoButton variant="outline-3" suppressTooltip onClick={refetch} className={actionClass}>
                  Try again
                </LureoButton>
                <span aria-hidden className="h-7" />
              </>
            ) : (
              <span aria-hidden className="h-[3.875rem]" />
            )}
          </div>
        </div>
      </div>

      {forSpectacles && link?.pinCode && (
        <div className="flex items-center gap-3 rounded-lureo-radius-pill border-lureo-border-width border-lureo-line bg-lureo-neutral-subtle py-1 pl-4 pr-1">
          <FieldLabel>Spectacles code</FieldLabel>
          <p className="font-lureo-font-mono font-semibold text-lureo-14 tracking-[0.3em] text-lureo-ink leading-none">{link.pinCode}</p>
          <CopyCodeButton copied={copied === 'code'} onClick={() => copyText(link.pinCode!, 'code')} className="ml-auto" />
        </div>
      )}
    </div>
  );
}
